import { useState, useEffect, useRef } from 'react';
import { client } from '../../sdkAdapter';
import MediaEditModal from './MediaEditModal';
import Swal from 'sweetalert2';
import DatePicker from '../form/date-picker';
import Pagination from '../ui/pagination';

interface MediaFile {
    id: number;
    name: string;
    title?: string;
    description?: string;
    path: string;
    type: string;
    folder: string;
    thumb_s?: string;
    thumb_m?: string;
    thumb_r?: string;
    size?: number;
    url?: string;
    extension?: string;
    created_at?: string;
    updated_at?: string;
}

const FOLDERS = ['all', 'products', 'categories', 'brands', 'banners', 'pages', 'footers'] as const;

export default function MediaGrid() {
    const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
    const [filteredFiles, setFilteredFiles] = useState<MediaFile[]>([]);
    const [loading, setLoading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedFolder, setSelectedFolder] = useState<string>('all');
    const [selectedType, setSelectedType] = useState<string>('all');
    const [selectedDate, setSelectedDate] = useState<string>('');
    const [editingFile, setEditingFile] = useState<MediaFile | null>(null);
    const [viewingImage, setViewingImage] = useState<string | null>(null);

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(30);
    const [totalItems, setTotalItems] = useState(0);

    const fileInputRef = useRef<HTMLInputElement>(null);

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            fetchData();
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm, selectedFolder, selectedType, selectedDate, currentPage, rowsPerPage]);

    const fetchData = async () => {
        setLoading(true);
        try {
            let res;
            const commonParams = {
                page: currentPage,
                per_page: rowsPerPage,
            };

            // Use server-side search if filters are active
            if (searchTerm || selectedFolder !== 'all' || selectedType !== 'all' || selectedDate) {
                const searchParams: any = { ...commonParams };
                if (searchTerm) searchParams.name = searchTerm;
                if (selectedFolder !== 'all') searchParams.folder = selectedFolder;
                if (selectedType !== 'all') searchParams.type = selectedType;
                if (selectedDate) searchParams.date = selectedDate;

                res = await client.getApiMediaSearch(undefined, {
                    params: searchParams
                }) as any;
            } else {
                res = await client.getApiMediaFiles(undefined, {
                    params: commonParams
                }) as any;
            }

            const responseData = res.data;
            let files = [];
            let total = 0;

            if (responseData?.data?.data) {
                files = responseData.data.data;
                total = responseData.data.total;
            } else if (responseData?.data) {
                files = Array.isArray(responseData.data) ? responseData.data : (responseData.data.data || []);
                total = responseData.data.total || files.length;
            } else {
                files = Array.isArray(responseData) ? responseData : [];
                total = files.length;
            }

            const mappedFiles = files.map((file: any) => ({
                ...file,
                // Derive extension if missing
                extension: file.extension || file.name?.split('.').pop()?.toLowerCase() || 'file',
                // Size handling
                size: file.size || (file.details ? parseInt(file.details) : undefined)
            }));

            setMediaFiles(mappedFiles);
            setFilteredFiles(mappedFiles);
            setTotalItems(total);
        } catch (err) {
            console.error('Failed to fetch media files:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (value: string) => {
        setSearchTerm(value);
        setCurrentPage(1);
    };

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setIsUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('folder', selectedFolder === 'all' ? 'products' : selectedFolder);

            await client.postApiMediaFileUpload(
                { postApiMediaFileUploadRequest: formData as any }
            );
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'success',
                title: 'File uploaded successfully',
                showConfirmButton: false,
                timer: 2000,
                timerProgressBar: true
            });

            fetchData();
        } catch (err: any) {
            console.error('Upload failed:', err);
            Swal.fire({
                title: 'Error',
                text: 'Failed to upload file. Please check file size and try again.',
                icon: 'error'
            });
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleDelete = async (id: number) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "This action cannot be undone!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#6b7280',
            confirmButtonText: 'Yes, delete it!',
            background: document.documentElement.classList.contains('dark') ? '#1f2937' : '#ffffff',
            color: document.documentElement.classList.contains('dark') ? '#ffffff' : '#1f2937',
        });

        if (result.isConfirmed) {
            try {
                // Try standard SDK call
                await client.postApiMediaFileDelete({
                    postApiMediaFileDeleteRequest: { id: id }
                });

                Swal.fire({
                    title: 'Deleted!',
                    text: 'Media file has been removed.',
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false,
                    toast: true,
                    position: 'top-end'
                });
                fetchData();
            } catch (err: any) {
                console.error('Delete failed:', err);

                // Fallback attempt for different SDK/API expectations
                try {
                    await (client as any).postApiMediaFileDelete({ id });
                    fetchData();
                    return;
                } catch (fallbackErr) {
                    console.error('Fallback delete failed:', fallbackErr);
                }

                const errorMessage = err.response?.data?.messages || err.response?.data?.message || err.message || 'Failed to delete file. It might be used elsewhere.';

                Swal.fire({
                    title: 'Error',
                    text: typeof errorMessage === 'string' ? errorMessage : 'Failed to delete file. Please check if the file exists on the server.',
                    icon: 'error'
                });
            }
        }
    };

    const handleEdit = (file: MediaFile) => {
        setEditingFile(file);
    };

    const handleCopyUrl = (file: MediaFile) => {
        const url = getFullUrl(file.path || file.url);
        if (url) {
            navigator.clipboard.writeText(url).then(() => {
                Swal.fire({
                    title: 'Copied!',
                    text: 'URL copied to clipboard!',
                    icon: 'success',
                    timer: 1500,
                    showConfirmButton: false,
                    toast: true,
                    position: 'top-end'
                });
            });
        }
    };

    const getFullUrl = (url: string | undefined) => {
        if (!url) return undefined;
        if (url.startsWith('http')) return url;

        const baseUrl = (import.meta.env.VITE_API_BASE || 'http://127.0.0.1:8000').replace(/\/$/, '');
        const cleanPath = url.replace(/^\//, '');

        // Robust handling for storage vs uploads root
        // If it starts with storage/ it's already structured correctly for the symbolic link
        // If it starts with uploads/ (common for thumbs), we check if it needs /storage prepended
        // But based on backend config, public/uploads/thumbs exists, so /uploads/... should work
        return `${baseUrl}/${cleanPath}`;
    };

    return (
        <div className="space-y-6">
            {/* Action Bar */}
            <div className="bg-white dark:bg-gray-800 p-4 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm flex flex-col xl:flex-row gap-4 justify-between items-stretch transition-all">
                <div className="flex flex-wrap items-center gap-3">
                    {/* Search */}
                    <div className="relative group flex-1 sm:flex-initial min-w-[200px]">
                        <input
                            type="text"
                            placeholder="Search files..."
                            value={searchTerm}
                            onChange={(e) => handleSearch(e.target.value)}
                            className="w-full px-4 py-2.5 pl-11 border border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50/50 dark:bg-gray-900/50 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none transition-all placeholder:text-gray-400"
                        />
                        <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-brand-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                    </div>

                    {/* Filters */}
                    <select
                        value={selectedFolder}
                        onChange={(e) => {
                            setSelectedFolder(e.target.value);
                            setCurrentPage(1);
                        }}
                        className="px-4 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50/50 dark:bg-gray-900/50 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-brand-500/20 transition-all cursor-pointer"
                    >
                        {FOLDERS.map(folder => (
                            <option key={folder} value={folder}>
                                {folder === 'all' ? 'All Folders' : folder.charAt(0).toUpperCase() + folder.slice(1)}
                            </option>
                        ))}
                    </select>

                    <select
                        value={selectedType}
                        onChange={(e) => {
                            setSelectedType(e.target.value);
                            setCurrentPage(1);
                        }}
                        className="px-4 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50/50 dark:bg-gray-900/50 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-brand-500/20 transition-all cursor-pointer"
                    >
                        <option value="all">All Types</option>
                        <option value="image">Images</option>
                        <option value="video">Videos</option>
                        <option value="audio">Audio</option>
                        <option value="doc">Documents</option>
                    </select>

                    <div className="w-[180px]">
                        <DatePicker
                            id="media-date-filter"
                            placeholder="Filter by date"
                            onChange={(_dates, dateStr) => {
                                setSelectedDate(dateStr);
                                setCurrentPage(1);
                            }}
                            defaultDate={selectedDate || undefined}
                        />
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    {loading && (
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-brand-50 dark:bg-brand-500/10 text-brand-500 rounded-full text-xs font-semibold animate-pulse">
                            <div className="w-1.5 h-1.5 bg-brand-500 rounded-full animate-ping"></div>
                            Syncing...
                        </div>
                    )}
                    <input
                        type="file"
                        ref={fileInputRef}
                        className="hidden"
                        onChange={handleFileChange}
                        accept=".csv,.txt,.xls,.pdf,.jpg,.jpeg,.png,.svg,.mp4,.avi,.mov,.mp3,image/*,video/*,audio/*"
                    />
                    <button
                        onClick={handleUploadClick}
                        disabled={isUploading}
                        className="flex items-center gap-2 px-6 py-2.5 bg-brand-500 text-white rounded-xl hover:bg-brand-600 shadow-lg shadow-brand-500/20 hover:shadow-brand-500/40 transition-all active:scale-95 disabled:opacity-50 whitespace-nowrap font-bold"
                    >
                        {isUploading ? (
                            <>
                                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                </svg>
                                Uploading...
                            </>
                        ) : (
                            <>
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                </svg>
                                Upload File
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* Results Legend */}
            {(searchTerm || selectedFolder !== 'all' || selectedType !== 'all') && (
                <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 ml-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Found {totalItems} total files.</span>
                    {(searchTerm || selectedFolder !== 'all' || selectedType !== 'all' || selectedDate) && (
                        <button
                            onClick={() => {
                                setSearchTerm('');
                                setSelectedFolder('all');
                                setSelectedType('all');
                                setSelectedDate('');
                                setCurrentPage(1);
                            }}
                            className="text-brand-500 hover:underline font-semibold ml-2"
                        >
                            Reset filters
                        </button>
                    )}
                </div>
            )}

            {/* Grid Container */}
            {loading && mediaFiles.length === 0 ? (
                <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-6">
                    {[...Array(10)].map((_, i) => (
                        <div key={i} className="aspect-square bg-gray-100 dark:bg-gray-800 rounded-2xl animate-pulse"></div>
                    ))}
                </div>
            ) : filteredFiles.length === 0 ? (
                <div className="text-center py-20 bg-white dark:bg-gray-800 rounded-3xl border border-dashed border-gray-200 dark:border-gray-700 shadow-inner group">
                    <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-50 dark:bg-gray-900 rounded-full mb-4 group-hover:scale-110 transition-transform duration-500">
                        <svg className="h-10 w-10 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                    </div>
                    <h4 className="text-lg font-bold text-gray-700 dark:text-gray-300">No results found</h4>
                    <p className="text-gray-500 dark:text-gray-500 mt-1 max-w-xs mx-auto text-sm">
                        {searchTerm || selectedFolder !== 'all'
                            ? "Try adjusting your filters or search terms to find what you're looking for."
                            : "Your library is empty. Start by uploading some amazing content!"}
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-6">
                    {filteredFiles.map(file => (
                        <div
                            key={file.id}
                            className="group relative bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col"
                        >
                            {/* Preview Area */}
                            <div className="aspect-square bg-gray-100 dark:bg-gray-950 flex items-center justify-center overflow-hidden relative cursor-pointer"
                                onClick={() => file.type?.startsWith('image') && setViewingImage(getFullUrl(file.path || file.url) || null)}
                            >
                                {file.type?.startsWith('image') ? (
                                    <img
                                        src={getFullUrl(file.path || file.thumb_m || file.url)}
                                        alt={file.name}
                                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                        onError={(e) => {
                                            e.currentTarget.style.display = 'none';
                                            e.currentTarget.parentElement?.classList.add('flex', 'items-center', 'justify-center');
                                            const icon = document.createElement('div');
                                            icon.className = 'text-4xl';
                                            icon.textContent = '🖼️';
                                            e.currentTarget.parentElement?.appendChild(icon);
                                        }}
                                    />
                                ) : (
                                    <div className="flex flex-col items-center gap-2 p-6 text-center">
                                        <div className="w-16 h-16 rounded-2xl bg-gray-50 dark:bg-gray-900 flex items-center justify-center text-3xl shadow-inner group-hover:rotate-6 transition-transform">
                                            {file.type === 'video' ? '🎬' : file.type === 'audio' ? '🎵' : '📄'}
                                        </div>
                                        <span className="text-[10px] font-black uppercase tracking-widest text-gray-400 bg-gray-200/50 dark:bg-gray-700/50 px-2 py-0.5 rounded-full">
                                            {file.extension}
                                        </span>
                                    </div>
                                )}

                                {/* Overlay UI */}
                                <div className="absolute inset-0 bg-brand-900/40 backdrop-blur-[2px] opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center gap-3">
                                    <button
                                        onClick={(e) => { e.stopPropagation(); handleEdit(file); }}
                                        className="w-10 h-10 bg-white/95 text-gray-800 rounded-full flex items-center justify-center hover:bg-brand-500 hover:text-white transition-all transform hover:scale-110 active:scale-90"
                                        title="Edit Info"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                        </svg>
                                    </button>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); handleCopyUrl(file); }}
                                        className="w-10 h-10 bg-white/95 text-gray-800 rounded-full flex items-center justify-center hover:bg-brand-500 hover:text-white transition-all transform hover:scale-110 active:scale-90"
                                        title="Copy Direct Link"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                                        </svg>
                                    </button>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); handleDelete(file.id); }}
                                        className="w-10 h-10 bg-white/95 text-red-600 rounded-full flex items-center justify-center hover:bg-red-600 hover:text-white transition-all transform hover:scale-110 active:scale-90"
                                        title="Delete Permanently"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                    </button>
                                </div>

                                {/* Selection Badge or Type Indicator */}
                                <div className="absolute top-3 left-3 flex gap-2">
                                    <span className="bg-black/50 backdrop-blur-md text-white text-[10px] font-bold px-2 py-1 rounded-lg uppercase">
                                        {file.folder}
                                    </span>
                                </div>
                            </div>

                            {/* Info Area */}
                            <div className="p-4 border-t border-gray-50 dark:border-gray-700/50 flex-1 flex flex-col justify-between">
                                <div className="space-y-1">
                                    <p className="text-sm font-bold text-gray-900 dark:text-white truncate" title={file.title || file.name}>
                                        {file.title || file.name}
                                    </p>
                                    <p className="text-[10px] text-gray-400 font-medium truncate italic" title={file.name}>
                                        {file.name}
                                    </p>
                                </div>

                                <div className="flex justify-between items-center mt-3 pt-2 border-t border-gray-50 dark:border-gray-700/30">
                                    <div className="flex items-center gap-2">
                                        <span className="w-1.5 h-1.5 rounded-full bg-brand-500"></span>
                                        <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400">
                                            {file.size ? (file.size > 1024 * 1024 ? `${(file.size / (1024 * 1024)).toFixed(1)} MB` : `${(file.size / 1024).toFixed(0)} KB`) : '---'}
                                        </span>
                                    </div>
                                    <span className="text-[10px] font-medium text-gray-400">
                                        {file.created_at ? new Date(file.created_at).toLocaleDateString() : ''}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Pagination Controls */}
            {totalItems > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden mt-6">
                    <Pagination
                        currentPage={currentPage}
                        totalPages={Math.ceil(totalItems / rowsPerPage)}
                        onPageChange={setCurrentPage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(rows) => {
                            setRowsPerPage(rows);
                            setCurrentPage(1);
                        }}
                        totalItems={totalItems}
                    />
                </div>
            )}

            {/* Edit Modal */}
            <MediaEditModal
                file={editingFile}
                isOpen={!!editingFile}
                onClose={() => setEditingFile(null)}
                onSave={() => {
                    fetchData();
                    setEditingFile(null);
                }}
            />

            {/* Image Viewer Overlay */}
            {viewingImage && (
                <div
                    className="fixed inset-0 bg-brand-950/95 backdrop-blur-md flex items-center justify-center z-[99999] p-4 lg:p-10 animate-in fade-in zoom-in duration-300"
                    onClick={() => setViewingImage(null)}
                >
                    <button
                        onClick={() => setViewingImage(null)}
                        className="absolute top-6 right-6 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white rounded-full transition-all group"
                    >
                        <svg className="w-6 h-6 group-hover:rotate-90 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>

                    <div className="relative max-w-full max-h-full flex items-center justify-center shadow-2xl rounded-2xl overflow-hidden border border-white/10">
                        <img
                            src={viewingImage}
                            alt="Preview Full Size"
                            className="max-w-full max-h-full object-contain pointer-events-none"
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}
